import { type CarrierName, Currency } from "@inrev/common";
import { carrierLogoIconMap } from "../../../utils/carrier";
import { byCarrierPriority, type CarrierPriorityInput, carrierDisplayName } from "./carrierDisplay";
import type { BondingLineCarrier, BondingLineNode } from "./types";

const TOTAL_LINE_ID = "total";
const TOTAL_LINE_LABEL = "Total Bonding Line";
const IN_REVIEW_LABEL = "In Review";
const PER_REQUEST_LABEL = "Per Request";

export type BondingLineValues = {
	rate?: number | null;
	singleLimit?: number | null;
	aggregateLimit?: number | null;
};

export type ContractExposureBuckets = {
	bid: { total: number };
	bid_to_final: { total: number };
	final: { total: number };
	total?: number;
};

export type CarrierBondingLineInput = {
	carrierName: CarrierName;
};

export const EMPTY_CARRIER_BONDING_LINES: CarrierBondingLineInput[] = [];

export const usedContractExposure = (exposure: ContractExposureBuckets): number =>
	exposure.total ??
	Currency.sum([exposure.bid.total, exposure.bid_to_final.total, exposure.final.total]);

const totalLine = (values: BondingLineValues, used?: number): BondingLineNode => ({
	id: TOTAL_LINE_ID,
	label: TOTAL_LINE_LABEL,
	rate: values.rate,
	singleLimit: values.singleLimit,
	aggregateLimit: values.aggregateLimit,
	used,
	emphasized: true,
});

const withUsedError = (line: BondingLineNode, usedError?: Error): BondingLineNode[] => [
	usedError === undefined ? line : { ...line, usedError },
];

export const mapAdminBondingLines = (
	bondingLine: BondingLineValues | undefined,
	exposure: ContractExposureBuckets | undefined,
	usedError?: Error,
): BondingLineNode[] =>
	withUsedError(
		totalLine(
			bondingLine ?? {},
			exposure === undefined ? undefined : usedContractExposure(exposure),
		),
		usedError,
	);

export const mapAdminBondingLineCarriers = (
	carrierBondingLines: CarrierBondingLineInput[],
	carriers: CarrierPriorityInput[],
): BondingLineCarrier[] =>
	[...carrierBondingLines].sort(byCarrierPriority(carriers)).map((line) => ({
		id: line.carrierName,
		name: carrierDisplayName[line.carrierName] ?? line.carrierName,
		logo: carrierLogoIconMap[line.carrierName],
	}));

export const mapAgentBondingLines = (
	bondingLine: BondingLineValues,
	isReview: boolean,
	used?: number,
	usedError?: Error,
): BondingLineNode[] => {
	if (isReview) {
		return withUsedError(
			{
				...totalLine(bondingLine, used),
				rateLabel: IN_REVIEW_LABEL,
				singleLimitLabel: IN_REVIEW_LABEL,
				aggregateLimitLabel: IN_REVIEW_LABEL,
			},
			usedError,
		);
	}

	return withUsedError(
		{
			...totalLine(bondingLine, used),
			singleLimitLabel: bondingLine.singleLimit === undefined ? PER_REQUEST_LABEL : undefined,
			aggregateLimitLabel: bondingLine.aggregateLimit === undefined ? PER_REQUEST_LABEL : undefined,
		},
		usedError,
	);
};
